import type { OmniSearchResponse, OmniSearchResult } from '@consumer/types/serializers'
import {
  faGift,
  faArrowTrendUp,
  faBaby,
  faBagShopping,
  faBaguette,
  faBasketShoppingSimple,
  faBed,
  faBeerMug,
  faBicycle,
  faBookOpen,
  faBootHeeled,
  faBowlChopsticks,
  faBowlChopsticksNoodles,
  faBowlHot,
  faBowlRice,
  faBowlingBallPin,
  faBuildingColumns,
  faBurgerCheese,
  faBurgerFries,
  faBurrito,
  faCakeSlice,
  faCampground,
  faCar,
  faCarWash,
  faCards,
  faCarrot,
  faCartShopping,
  faChampagneGlasses,
  faCheeseSwiss,
  faCocktail,
  faCoffeeBeans,
  faCompass,
  faCourtSport,
  faCupcake,
  faCustard,
  faDog,
  faDolphin,
  faDrumstickBite,
  faDumbbell,
  faEggFried,
  faFaceKissWinkHeart,
  faFaceParty,
  faFaceRelieved,
  faFaceSurprise,
  faFalafel,
  faFerrisWheel,
  faFilm,
  faFishCooked,
  faFishingRod,
  faFootball,
  faGarage,
  faGasPump,
  faGaugeHigh,
  faGolfClub,
  faGolfFlagHole,
  faGun,
  faHandHeart,
  faHandSparkles,
  faHatWizard,
  faHeadphones,
  faHeart,
  faHorseSaddle,
  faHotel,
  faHouseChimneyHeart,
  faHouseMedical,
  faHouseTree,
  faIceCream,
  faLandmarkDome,
  faLaptop,
  faLightCeiling, faMailbox,
  faMapLocation,
  faMarsDouble,
  faMaskSnorkel,
  faMasksTheater,
  faMeat,
  faMedal,
  faMicrophoneStand,
  faMonkey,
  faMountain,
  faMugHot,
  faMugTea,
  faMustache,
  faOliveBranch,
  faPanFood,
  faParachuteBox,
  faPepperHot,
  faPersonPinball,
  faPersonSkating,
  faPersonSkiing,
  faPersonSwimming,
  faPersonWalking,
  faPickaxe,
  faPizza,
  faPlane, faPretzel,
  faRingDiamond,
  faRingsWedding,
  faRv,
  faSailboat,
  faSandwich,
  faSausage,
  faScissors,
  faShirtLongSleeve,
  faShirtRunning,
  faShirtTankTop,
  faShop,
  faSpa,
  faStar,
  faSteak,
  faSteeringWheel,
  faSun,
  faSushi,
  faTaco,
  faTeddyBear,
  faTennisBall,
  faTentDoublePeak,
  faTruckUtensils,
  faUniformMartialArts,
  faUserMusic,
  faUserPen,
  faVanShuttle,
  faWater,
  faWaterLadder,
  faWheat,
  faWineBottle,
  faUtensils,
} from '@fortawesome/pro-light-svg-icons'

import {
  faLocationDot,
} from '@fortawesome/duotone-light-svg-icons'

import { capitalize, upperFirst } from 'lodash-es'
import OmniSearchApi from '@consumer/api/OmniSearchApi'
import { openCustomizerModal } from '@consumer/services/modals'
import { trackEvent } from '@shared/tracking'
import type { GIconDefinition } from '@consumer/components/GIcon.vue'
import OmniSearchHitsApi from '@consumer/api/OmniSearchHitsApi'
import { customizerPathFromIdea } from '@consumer/components/GiftlyIdea.vue'
import genericPlace from './generic-place.svg'
import type { GenericResult, HotWord, HotWordContent, RecentSearchResult, Result, SearchResult } from './types'
import miniMap from './minimap-empty.png'

export const genericPlaceImage = genericPlace

export function defaultIconFor (result: OmniSearchResult): GIconDefinition {
  const iconMap: Record<OmniSearchResult['searchableType'], GIconDefinition> = {
    Place: result.imageUrl ? undefined : faShop,
    FeaturedCity: result.imageUrl ? undefined : faLocationDot,
    List: result.imageUrl ? undefined : faShop,
    GiftIdeaLanding: faGift,
    GiftIdea: faGift,
    GiftCardCategory: iconForCategory(
      result.imageUrl?.split('/').pop()?.split('.')?.[0]?.split('-')?.slice(0, -1)?.join('-'),
    ),
    MultiplaceGift: faGift,
    SubtopicBrand: faGift,
    LandingPage: result.imageUrl ? undefined : faGift,
  }
  return iconMap[result.searchableType]
}

export function iconForCategory (categoryName?: string): GIconDefinition {
  const iconMap: Record<string, GIconDefinition> = {
    'american-restaurant': faBurgerCheese,
    'amusement-park': faFerrisWheel,
    'asian-fusion-restaurant': faBowlChopsticksNoodles,
    'barbecue-restaurant': faMeat,
    'bed-breakfast': faBed,
    'bike-rental': faBicycle,
    'brazilian-restaurant': faMeat,
    'breakfast-brunch': faEggFried,
    'burger-restaurant': faBurgerFries,
    'car-rental': faCar,
    'car-wash': faCarWash,
    'chinese-restaurant': faBowlChopsticksNoodles,
    'comic-book': faBookOpen,
    'dance-studio': faPersonWalking,
    'day-spa': faMugTea,
    'department-store': faShop,
    'dessert-restaurant': faCakeSlice,
    'ethiopian-restaurant': faPanFood,
    'farmers-market': faWheat,
    'fast-food': faBurgerFries,
    'fine-dining': faChampagneGlasses,
    'food-delivery': faTruckUtensils,
    'food-stand': faDrumstickBite,
    'french-restaurant': faBaguette,
    'gay-bar': faMarsDouble,
    'german-restaurant': faSausage,
    'go-kart': faSteeringWheel,
    'going-out': faCocktail,
    'greek-restaurant': faOliveBranch,
    'hair-salon': faScissors,
    'historical-site': faLandmarkDome,
    'home-decor': faLightCeiling,
    'home-garden': faHouseTree,
    'home-improvement': faHouseChimneyHeart,
    'horse-racing': faHorseSaddle,
    'ice-cream': faIceCream,
    'indian-restaurant': faBowlRice,
    'italian-restaurant': faCustard,
    'japanese-restaurant': faBowlChopsticks,
    'korean-restaurant': faBowlHot,
    'latin-restaurant': faTaco,
    'martial-arts': faUniformMartialArts,
    'mens-clothing': faShirtLongSleeve,
    'mexican-restaurant': faBurrito,
    'middle-eastern-restaurant': faFalafel,
    'mini-golf': faGolfFlagHole,
    'movie-theater': faFilm,
    'nail-salon': faHandSparkles,
    'new-restaurant': faStar,
    'opera-ballet': faFaceSurprise,
    'performing-arts': faHatWizard,
    'pizza-restaurant': faPizza,
    'pro-sports': faMedal,
    'race-track': faGaugeHigh,
    'rafting-kayaking': faWater,
    'rec-center': faWaterLadder,
    'romantic-restaurant': faHeart,
    'sandwich-restaurant': faSandwich,
    'seafood-restaurant': faFishCooked,
    'skating-rink': faPersonSkating,
    'ski-resort': faPersonSkiing,
    'southern-restaurant': faCupcake,
    'spa-beauty': faSpa,
    'sporting-goods': faShirtRunning,
    'sports-club': faCourtSport,
    'steakhouse-restaurant': faSteak,
    'summer-camp': faRv,
    'sushi-restaurant': faSushi,
    'tapas-restaurant': faCheeseSwiss,
    'thai-restaurant': faPepperHot,
    'toy-store': faTeddyBear,
    'trendy-restaurant': faArrowTrendUp,
    'vegetarian-restaurant': faCarrot,
    'womens-clothing': faBootHeeled,
    activity: faFootball,
    airline: faPlane,
    aquarium: faDolphin,
    arcade: faPersonPinball,
    baby: faBaby,
    bakery: faPretzel,
    bar: faChampagneGlasses,
    barber: faMustache,
    boating: faSailboat,
    book: faBookOpen,
    bowling: faBowlingBallPin,
    bridal: faRingsWedding,
    cafe: faCoffeeBeans,
    campground: faCampground,
    casino: faCards,
    climbing: faPickaxe,
    coffee: faMugHot,
    comedy: faMasksTheater,
    computer: faLaptop,
    concert: faMicrophoneStand,
    diving: faMaskSnorkel,
    drugstore: faHouseMedical,
    electronics: faHeadphones,
    facial: faFaceRelieved,
    festival: faFaceParty,
    fishing: faFishingRod,
    fitness: faDumbbell,
    flower: faPlane,
    gas: faGasPump,
    gastropub: faBeerMug,
    golf: faGolfClub,
    grocery: faCartShopping,
    gym: faGarage,
    hotel: faHotel,
    jewelry: faRingDiamond,
    lingerie: faShirtTankTop,
    makeup: faFaceKissWinkHeart,
    mall: faMailbox,
    massage: faHandHeart,
    museum: faBuildingColumns,
    musical: faUserMusic,
    outdoor: faMountain,
    paintball: faGun,
    pet: faDog,
    pilates: faMustache,
    pub: faBeerMug,
    restaurant: faUtensils,
    retail: faBasketShoppingSimple,
    shopping: faBagShopping,
    show: faTentDoublePeak,
    skydiving: faParachuteBox,
    soccer: faFootball,
    sports: faFootball,
    swimming: faPersonSwimming,
    tanning: faSun,
    tattoo: faUserPen,
    tennis: faTennisBall,
    tour: faMapLocation,
    transportation: faVanShuttle,
    travel: faCompass,
    winery: faWineBottle,
    yoga: faFaceRelieved,
    zoo: faMonkey,
  }

  return (categoryName && iconMap[categoryName]) || faShop
}

export function hintFor (result: OmniSearchResult, searchResponse?: OmniSearchResponse): string | undefined {
  const hintMap: Record<OmniSearchResult['searchableType'], string | undefined> = {
    Place: result.location,
    FeaturedCity: undefined,
    List: 'Nationwide',
    GiftIdeaLanding: undefined,
    GiftIdea: undefined,
    GiftCardCategory: undefined,
    MultiplaceGift: hintForMultiplace(result, searchResponse),
    SubtopicBrand: undefined,
    LandingPage: undefined,
  }

  return hintMap[result.searchableType]
}

function hintForMultiplace (result: OmniSearchResult, searchResponse?: OmniSearchResponse): string {
  if (
    searchResponse?.results[0].text
    && searchResponse?.results[0] !== result
    && result.content?.includes(searchResponse?.results[0].text.toLocaleLowerCase())
  ) {
    return `Multi-Place - includes ${searchResponse.results[0].text}`
  }
  else {
    return 'Multi-Place'
  }
}

export function backgroundFor (result: OmniSearchResult, city?: OmniSearchResult): string | undefined {
  let backgroundUrl: string | undefined

  if (result.searchableType === 'FeaturedCity') {
    backgroundUrl = result.imageUrl || miniMap
  }
  else if (result.searchableType === 'GiftCardCategory') {
    // When there's a location in the search response and a city was found for that location, we use the city image as
    // the background. Otherwise, we use the mini-map as a fallback.
    backgroundUrl = city && result.location ? (city?.imageUrl || miniMap) : undefined
  }
  else {
    backgroundUrl = result.imageUrl
  }

  // If the background URL yields an error when being loaded, unset it from the result so that we render the defaults.
  if (backgroundUrl) {
    const img = new Image()
    img.src = backgroundUrl
    img.onerror = () => {
      result.imageUrl = undefined
    }
  }

  return backgroundUrl
}

export function textFor (result: OmniSearchResult): string {
  if (result.searchableType === 'SubtopicBrand') {
    return `${result.text} gifts`
  }

  if (result.searchableType === 'GiftCardCategory') {
    return `${upperFirst(result.text)} ${result.location ? `in ${result.location}` : 'nationwide'}`
  }

  return result.text
}

export function goToSearch (search?: SearchResult['search'], query?: string) {
  const { category, city } = search || {}

  if (!category?.trim().length && !city?.trim().length && !query?.trim().length) return

  const clickedOn = search ? `${search?.category} in ${search?.city}` : query

  trackEvent(
    'click',
    'omni-search-hit',
    JSON.stringify({
      query,
      clickedOn,
    }),
  )

  OmniSearchHitsApi.create({
    data: {
      query,
      text: clickedOn,
    },
  })

  const params = new URLSearchParams()

  if (category || query)
    params.set('category', category || query!)

  if (city)
    params.set('city', city)

  window.location.href = `/search?${params.toString()}`
}

function goToResult (result: OmniSearchResult, query = '') {
  const clickedOn = textFor(result)

  trackEvent(
    'click',
    'omni-search-hit',
    JSON.stringify({
      query,
      clickedOn,
      searchableId: result.searchableId,
      searchableType: result.searchableType,
    }),
  )

  OmniSearchHitsApi.create({
    data: {
      query,
      text: clickedOn,
      searchableId: result.searchableId,
      searchableType: result.searchableType,
    },
  })

  window.location.href = result.searchableType === 'GiftIdea'
    ? customizerPathFromIdea(result.giftIdea)
    : OmniSearchApi.show.path({
      ...result,
      query: { city: result.location, query },
    })
}

function goToGeneric (tagline: string, query = '') {
  const clickedOn = `${tagline} (works anywhere)`

  trackEvent('click', 'omni-search-hit', JSON.stringify({ query, clickedOn }))

  OmniSearchHitsApi.create({
    data: {
      query,
      text: clickedOn,
    },
  })

  openCustomizerModal({
    validateTaglineOnMount: true,
    gift: {
      amount: 100,
      giftlyMetadata: { tagline },
    },
  })
}

function goToHotWord (hotWord: HotWordContent, query = '') {
  trackEvent('click', 'omni-search-hit', JSON.stringify({ query, clickedOn: hotWord.text }))

  OmniSearchHitsApi.create({
    data: {
      query,
      text: hotWord.text,
    },
  })

  window.location.href = hotWord.url
}

export function navigateToResult (searchResult?: Result, query?: string) {
  if (!searchResult || searchResult === 'all') return

  if ('search' in searchResult) {
    goToSearch(searchResult.search, query)
  }
  else if ('generic' in searchResult) {
    goToGeneric(searchResult.generic.tagline, query)
  }
  else if ('hotWord' in searchResult) {
    goToHotWord(searchResult.hotWord, query)
  }
  else if ('recentSearch' in searchResult) {
    // Do nothing. We'll handle this in Result.vue
  }
  else {
    goToResult(searchResult, query)
  }
}

export function isRecentSearch (result: Result): result is RecentSearchResult {
  return result !== 'all' && 'recentSearch' in result
}

export function isGenericSearch (result: Result): result is GenericResult {
  return result !== 'all' && 'generic' in result
}

function buildHotWord (query: string) {
  const normalizedQuery = query.toLowerCase()
  let hotWord: HotWordContent | undefined

  if (['corp', 'corporate'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Corporate Gifts',
      url: '/corp',
    }
  }

  if (['egift'].includes(normalizedQuery)) {
    hotWord = {
      text: 'eGift Cards',
      url: '/lp/e-gift-card',
    }
  }

  if (['visa', 'visa gift card', 'visa gift cards'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Visa Gift Cards',
      url: '/buy-a-gift',
    }
  }

  if (['gift card', 'gift cards'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Buy a Gift Card',
      url: '/buy-a-gift',
    }
  }

  if (['login'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Login',
      url: '/login',
    }
  }

  if (['faq', 'faqs', 'support', 'help'].includes(normalizedQuery)) {
    hotWord = {
      text: normalizedQuery.includes('faq') ? 'FAQs' : capitalize(normalizedQuery),
      url: '/support',
    }
  }

  if (normalizedQuery.includes('redeem')) {
    hotWord = {
      text: 'Redeem a Gift Card',
      url: '/redeem',
    }
  }

  if (['customize'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Customize a Gift',
      url: '/customize-gift',
    }
  }

  if (['activate'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Activate your card',
      url: '/activate',
    }
  }

  if (['manage', 'card', 'balance'].includes(normalizedQuery)) {
    hotWord = {
      text: 'Access your card',
      url: '/card/access',
    }
  }

  return hotWord
}

export function detectHotWord (query: string) {
  const hotWord = buildHotWord(query)
  return hotWord ? { hotWord } as HotWord : undefined
}
